import type {
  BulkCreateRoomBookingsDTO,
  CreateRoomBookingDTO,
  UpdateRoomBookingDTO,
} from "@/modules/room-bookings/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roomBookingsApi } from "./client";

export const roomBookingsKeys = {
  all: ["room-bookings"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["room-bookings", "list", q] as const,
  detail: (id: string) => ["room-bookings", "detail", id] as const,
  assistInstructors: (id: string) =>
    ["room-bookings", "assist-instructors", id] as const,
  calendar: (
    start: string,
    end: string,
    filters?: { roomId?: string; status?: string },
  ) => ["room-bookings", "calendar", start, end, filters] as const,
};

export function useRoomBookingsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: roomBookingsKeys.list(q),
    queryFn: () => roomBookingsApi.list(q),
  });
}

export function useRoomBookingQuery(id: string) {
  return useQuery({
    queryKey: roomBookingsKeys.detail(id),
    queryFn: () => roomBookingsApi.getById(id),
    enabled: !!id,
  });
}

export function useRoomBookingsCalendarQuery(
  start: string,
  end: string,
  filters?: { roomId?: string; status?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: roomBookingsKeys.calendar(start, end, filters),
    queryFn: () => roomBookingsApi.getCalendar(start, end, filters),
    enabled: (options?.enabled ?? true) && !!start && !!end,
  });
}

export function useCreateRoomBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoomBookingDTO) => roomBookingsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງການຈອງຫ້ອງສຳເລັດ");
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
    onError: () => toast.error("ສ້າງການຈອງຫ້ອງບໍ່ສຳເລັດ"),
  });
}

export function useBulkCreateRoomBookings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkCreateRoomBookingsDTO) =>
      roomBookingsApi.bulkCreate(input),
    onSuccess: (result) => {
      if (result.conflicts.length > 0) {
        toast.error(`ພົບການຂັດແຍ້ງ ${result.conflicts.length} ລາຍການ`);
        return;
      }
      toast.success(`ສ້າງການຈອງ ${result.createdCount} ລາຍການສຳເລັດ`);
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
    onError: () => toast.error("ສ້າງການຈອງຫ້ອງບໍ່ສຳເລັດ"),
  });
}

export function useUpdateRoomBooking(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRoomBookingDTO) =>
      roomBookingsApi.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດການຈອງຫ້ອງສຳເລັດ");
      qc.invalidateQueries({ queryKey: roomBookingsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
    },
    onError: () => toast.error("ອັບເດດການຈອງຫ້ອງບໍ່ສຳເລັດ"),
  });
}

export function useAssistInstructorsQuery(bookingId: string) {
  return useQuery({
    queryKey: roomBookingsKeys.assistInstructors(bookingId),
    queryFn: () => roomBookingsApi.listAssistInstructors(bookingId),
    enabled: !!bookingId,
  });
}

export function useAddAssistInstructor(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructorId: string) =>
      roomBookingsApi.addAssistInstructor(bookingId, { instructorId }),
    onSuccess: () => {
      toast.success("ເພີ່ມຄູຊ່ວຍສອນແລ້ວ");
      qc.invalidateQueries({
        queryKey: roomBookingsKeys.assistInstructors(bookingId),
      });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "ເພີ່ມຄູຊ່ວຍສອນບໍ່ສຳເລັດ"),
  });
}

export function useRemoveAssistInstructor(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checkInId: string) =>
      roomBookingsApi.removeAssistInstructor(bookingId, checkInId),
    onSuccess: () => {
      toast.success("\u0ea5\u0eb6\u0e9a\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99\u0ec1\u0ea5\u0ec9\u0ea7");
      qc.invalidateQueries({
        queryKey: roomBookingsKeys.assistInstructors(bookingId),
      });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "\u0ea5\u0eb6\u0e9a\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99\u0e9a\u0ecd\u0ec8\u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94"),
  });
}

export function useCompleteRoomBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      /** check-in ที่ถอดออก (ครูไม่มา) */
      removeCheckInIds?: string[];
      /** ครูช่วยที่เพิ่มตอน complete */
      assistInstructorIds?: string[];
    }) => {
      for (const checkInId of [...new Set(input.removeCheckInIds ?? [])]) {
        await roomBookingsApi.removeAssistInstructor(input.id, checkInId);
      }
      await roomBookingsApi.complete(input.id);
      const ids = [...new Set(input.assistInstructorIds ?? [])];
      for (const instructorId of ids) {
        await roomBookingsApi.addAssistInstructor(input.id, { instructorId });
      }
      return { id: input.id, assistCount: ids.length };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
      qc.invalidateQueries({
        queryKey: roomBookingsKeys.detail(result.id),
      });
      qc.invalidateQueries({
        queryKey: roomBookingsKeys.assistInstructors(result.id),
      });
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error ? e.message : "ສຳເລັດການຈອງບໍ່ສຳເລັດ",
      ),
  });
}

export function useCancelRoomBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => roomBookingsApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roomBookingsKeys.all });
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
  });
}
